import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, GraduationCap, Trophy, Users, Building2,
  Megaphone, Link2, Bell, ExternalLink, FileText, Cpu, FlaskConical, Rocket, ChevronLeft, ChevronRight,
  Award, Briefcase, BookOpen, Zap,
} from "lucide-react";
import { useCidco } from "./CidcoLayout";
import { STATIC_CONTENT, slugify } from "./_shared";
import RecentAcademicsWidget from "@/components/RecentAcademicsWidget";
import AlumniStrip from "@/components/AlumniStrip";

function toYoutubeEmbed(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    let id = "";
    if (host === "youtu.be") id = u.pathname.split("/").filter(Boolean)[0] || "";
    else if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      if (u.pathname === "/watch") id = u.searchParams.get("v") || "";
      else if (u.pathname.startsWith("/embed/")) id = u.pathname.split("/")[2] || "";
      else if (u.pathname.startsWith("/shorts/")) id = u.pathname.split("/")[2] || "";
    }
    return id ? `https://www.youtube.com/embed/${id}?rel=0` : "";
  } catch { return ""; }
}

// Rotating headline taglines for animated hero
const ROTATING = [
  "Engineering the future.",
  "Designed for industry.",
  "Built for innovation.",
  "Crafted for tomorrow.",
];

function AnimatedHero({ slides, instName }) {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [slideIdx, setSlideIdx] = useState(0);
  const list = (slides || []).length > 0 ? slides : null;

  useEffect(() => {
    const t = setInterval(() => setPhraseIdx((i) => (i + 1) % ROTATING.length), 3000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!list || list.length < 2) return;
    const t = setInterval(() => setSlideIdx((i) => (i + 1) % list.length), 6000);
    return () => clearInterval(t);
  }, [list]);

  // Use CMS header_slides if present
  if (list) {
    return (
      <section className="relative bg-[#06183A]" data-testid="cid-hero-slider">
        <div className="relative w-full overflow-hidden" style={{ aspectRatio: "1920 / 620" }}>
          {list.map((s, i) => (
            <a
              key={s.id}
              href={s.link_url || undefined}
              target={s.link_url ? "_blank" : undefined}
              rel="noopener noreferrer"
              className={`absolute inset-0 transition-opacity duration-700 ${i === slideIdx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            >
              <img src={s.image_url} alt={s.caption || ""} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B2447] via-[#0B2447]/40 to-transparent" />
              {s.caption && (
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-12">
                  <div className="max-w-7xl mx-auto">
                    <div className="text-[10px] tracking-[0.4em] uppercase font-semibold text-[#67E8F9] mb-2">{instName}</div>
                    <h2 className="font-['Poppins',sans-serif] font-bold text-white text-3xl md:text-6xl drop-shadow leading-tight max-w-3xl">{s.caption}</h2>
                  </div>
                </div>
              )}
            </a>
          ))}
          {list.length > 1 && (
            <>
              <button onClick={() => setSlideIdx((i) => (i - 1 + list.length) % list.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-[#FF6B1A] backdrop-blur text-white flex items-center justify-center transition-colors"
                aria-label="Previous"><ChevronLeft size={20} /></button>
              <button onClick={() => setSlideIdx((i) => (i + 1) % list.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-[#FF6B1A] backdrop-blur text-white flex items-center justify-center transition-colors"
                aria-label="Next"><ChevronRight size={20} /></button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                {list.map((_, i) => (
                  <button key={i} onClick={() => setSlideIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${i === slideIdx ? "bg-[#FF6B1A] w-8" : "bg-white/40 hover:bg-white/70 w-1.5"}`}
                    aria-label={`slide ${i + 1}`} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    );
  }

  // Animated split hero (no slides yet)
  return (
    <section className="relative bg-gradient-to-br from-[#0B2447] via-[#1B3567] to-[#06183A] text-white overflow-hidden" data-testid="cid-hero">
      <style>{`
        @keyframes cid-blob { 0%,100% { transform: translate(0,0) scale(1);} 33% { transform: translate(30px,-20px) scale(1.06);} 66% { transform: translate(-20px,30px) scale(0.96);} }
        @keyframes cid-rise { from { opacity: 0; transform: translateY(20px);} to { opacity: 1; transform: translateY(0);} }
        @keyframes cid-fade { from { opacity: 0; transform: translateY(8px);} to { opacity: 1; transform: translateY(0);} }
        .cid-rise { animation: cid-rise 800ms ease-out forwards; opacity: 0; }
        .cid-rise-1 { animation-delay: 100ms; }
        .cid-rise-2 { animation-delay: 250ms; }
        .cid-rise-3 { animation-delay: 400ms; }
        .cid-rise-4 { animation-delay: 550ms; }
        .cid-phrase { animation: cid-fade 400ms ease-out; }
      `}</style>
      {/* Animated blobs */}
      <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#06B6D4]/25 blur-3xl pointer-events-none"
           style={{ animation: "cid-blob 18s ease-in-out infinite" }} />
      <div className="absolute -bottom-40 -left-32 w-[32rem] h-[32rem] rounded-full bg-[#FF6B1A]/20 blur-3xl pointer-events-none"
           style={{ animation: "cid-blob 22s ease-in-out infinite reverse" }} />
      {/* Subtle dot grid */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '32px 32px',
      }} />

      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center py-20 md:py-28">
        <div>
          <div className="inline-flex items-center gap-2 cid-rise cid-rise-1 text-[10px] tracking-[0.4em] uppercase text-[#67E8F9] font-semibold border border-[#06B6D4]/40 bg-[#06B6D4]/10 px-3 py-1.5 rounded-full">
            <Sparkles size={11} strokeWidth={2.5} /> Admissions Open 2025–26
          </div>
          <h1 className="cid-rise cid-rise-2 mt-5 font-['Poppins',sans-serif] font-extrabold text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight">
            <span className="block">Where ideas</span>
            <span className="block">become </span>
            <span className="block">
              <span key={phraseIdx} className="cid-phrase inline-block bg-gradient-to-r from-[#06B6D4] via-[#67E8F9] to-[#FF6B1A] bg-clip-text text-transparent">
                {ROTATING[phraseIdx]}
              </span>
            </span>
          </h1>
          <p className="cid-rise cid-rise-3 mt-6 max-w-xl text-white/80 text-base md:text-lg leading-relaxed">
            {STATIC_CONTENT.about_intro}
          </p>
          <div className="cid-rise cid-rise-4 mt-8 flex items-center gap-3 flex-wrap">
            <Link to="admissions"
              className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#FF6B1A] to-[#E55400] hover:from-[#E55400] hover:to-[#C24A00] text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:shadow-2xl transition-all">
              Apply Now
              <ArrowRight size={16} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="departments"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white border border-white/30 px-6 py-3 rounded-lg transition-colors">
              Explore Programmes
            </Link>
          </div>
        </div>

        {/* Right collage: layered cards */}
        <div className="relative hidden lg:block">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-72 h-72 rounded-full bg-gradient-to-br from-[#06B6D4]/40 to-transparent blur-2xl" />
          </div>
          <div className="relative grid grid-cols-2 gap-4">
            <FloatCard
              icon={Cpu}
              accent="cyan"
              title="6+ Engineering Branches"
              sub="UG & PG programmes"
              delay="cid-rise-1"
            />
            <FloatCard
              icon={FlaskConical}
              accent="orange"
              title="Modern Labs"
              sub="Practical, project-driven"
              delay="cid-rise-2"
              className="translate-y-8"
            />
            <FloatCard
              icon={Rocket}
              accent="cyan"
              title="Innovation Cell"
              sub="Startups & incubation"
              delay="cid-rise-3"
              className="-translate-y-2"
            />
            <FloatCard
              icon={Briefcase}
              accent="orange"
              title="Industry Placements"
              sub="Trusted recruiters"
              delay="cid-rise-4"
              className="translate-y-6"
            />
          </div>
        </div>
      </div>

      {/* Marquee strip */}
      <div className="relative border-t border-white/10 bg-[#06183A]/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-6 text-xs text-white/70 overflow-x-auto">
          <span className="shrink-0 uppercase tracking-[0.3em] text-[10px] text-[#67E8F9] font-semibold">Recognised by</span>
          {["AICTE Approved", "DTE Maharashtra", "GS Mandal", "NAAC ready", "ISO Quality"].map((b) => (
            <span key={b} className="shrink-0 px-3 py-1 rounded-full bg-white/5 ring-1 ring-white/10">{b}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

function FloatCard({ icon: Icon, accent, title, sub, delay, className = "" }) {
  const bg = accent === "cyan" ? "from-[#06B6D4] to-[#0891B2]" : "from-[#FF6B1A] to-[#E55400]";
  return (
    <div className={`cid-rise ${delay} relative bg-white/10 hover:bg-white/15 backdrop-blur-md rounded-2xl p-5 ring-1 ring-white/20 transition-all hover:-translate-y-1 ${className}`}>
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${bg} flex items-center justify-center shadow-lg`}>
        <Icon size={20} strokeWidth={1.5} className="text-white" />
      </div>
      <div className="mt-3 text-white font-semibold text-sm leading-tight">{title}</div>
      <div className="text-[11px] text-white/70 mt-0.5">{sub}</div>
    </div>
  );
}

export default function Home() {
  const data = useCidco();
  const academics = data.academics || [];
  const inst = data.institute || {};
  const departments = (data.departments || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const admissions = data.notices_admission || [];
  const links = data.links || [];
  const notices = data.notices_general || [];
  const ytEmbed = toYoutubeEmbed(inst.youtube_url);

  const stats = [
    { icon: GraduationCap, n: inst.year_of_establishment ? `${Math.max(1, new Date().getFullYear() - Number(inst.year_of_establishment))}+` : "10+", l: "Years of Excellence", color: "from-[#0B2447] to-[#1B3567]" },
    { icon: BookOpen, n: `${departments.length || 0}`, l: "Departments", color: "from-[#06B6D4] to-[#0891B2]" },
    { icon: Building2, n: "AICTE", l: "Approved", color: "from-[#FF6B1A] to-[#E55400]" },
    { icon: Award, n: "GSM", l: "Flagship Campus", color: "from-[#1B3567] to-[#06183A]" },
  ];

  const pillars = [
    { icon: Cpu, title: "Industry-aligned curriculum", body: "Outcome-based learning, mapped to current technology stacks and skill demand." },
    { icon: FlaskConical, title: "Modern laboratories", body: "Hands-on practice with the equipment, software, and methodologies industry uses." },
    { icon: Rocket, title: "Innovation & startups", body: "Active incubation cell, hackathons, and project-led discovery from year one." },
    { icon: Users, title: "Faculty who mentor", body: "PhD-qualified, research-active faculty who coach, challenge, and unblock you." },
  ];

  return (
    <div data-testid="cid-home">
      <AnimatedHero slides={data.header_slides} instName={inst.name} />

      {/* Stat strip */}
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.l} className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.color} text-white flex items-center justify-center shadow-md`}>
                  <Icon size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-['Poppins',sans-serif] font-bold text-2xl text-[#0B2447]">{s.n}</div>
                  <div className="text-[11px] text-[#475569] uppercase tracking-[0.15em] font-semibold">{s.l}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Welcome / About preview */}
      <section className="py-16 md:py-24 bg-[#F4F6FA]">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#FF6B1A] font-semibold">
              Welcome to {inst.name || "MIT CIDCO"}
            </div>
            <h2 className="mt-2 font-['Poppins',sans-serif] font-bold text-3xl md:text-5xl text-[#0B2447] leading-[1.05]">
              A premier <span className="text-[#06B6D4]">engineering campus</span>
              {inst.year_of_establishment && (<>, <span className="text-[#FF6B1A]">since {inst.year_of_establishment}</span></>)}
            </h2>
            <p className="mt-5 text-[#475569] leading-relaxed">{STATIC_CONTENT.about_intro}</p>
            <Link to="about"
              className="mt-7 group inline-flex items-center gap-2 bg-[#0B2447] hover:bg-[#06183A] text-white px-6 py-3 rounded-lg text-sm font-semibold transition-colors">
              Read More
              <ArrowRight size={14} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-[#E2E8F0] bg-[#06183A]" data-testid="cid-home-video">
            <div className="relative w-full aspect-video">
              {ytEmbed ? (
                <iframe src={ytEmbed} title={`${inst.name || "Institute"} — Campus`} loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen className="absolute inset-0 w-full h-full border-0"
                        data-testid="cid-home-youtube" />
              ) : inst.home_image_url ? (
                <img src={inst.home_image_url} alt={inst.name || "Institute"} className="absolute inset-0 w-full h-full object-cover" loading="lazy"
                     data-testid="cid-home-image" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">
                  Campus video / image will appear here once configured.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Why pillars */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#06B6D4] font-semibold">Why MIT CIDCO</div>
            <h2 className="mt-2 font-['Poppins',sans-serif] font-bold text-3xl md:text-5xl text-[#0B2447] leading-tight">
              Built for ambitious engineers.
            </h2>
            <p className="mt-3 text-[#475569]">
              Four pillars that shape every classroom, laboratory, and project on campus.
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              const useCyan = i % 2 === 0;
              return (
                <div key={p.title}
                  className="group relative bg-[#F4F6FA] hover:bg-white rounded-2xl p-6 transition-all ring-1 ring-[#E2E8F0] hover:ring-[#06B6D4] hover:shadow-xl hover:-translate-y-1">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-md ${useCyan ? "bg-gradient-to-br from-[#06B6D4] to-[#0891B2]" : "bg-gradient-to-br from-[#FF6B1A] to-[#E55400]"}`}>
                    <Icon size={20} strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-4 font-['Poppins',sans-serif] font-semibold text-[#0B2447] leading-tight">{p.title}</h3>
                  <p className="mt-2 text-[12px] text-[#475569] leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Programmes preview */}
      {departments.length > 0 && (
        <section className="py-16 md:py-24 bg-[#F4F6FA]" data-testid="cid-home-departments">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#FF6B1A] font-semibold">Academics</div>
                <h2 className="mt-2 font-['Poppins',sans-serif] font-bold text-3xl md:text-5xl text-[#0B2447] leading-tight">
                  Departments &amp; Programmes
                </h2>
              </div>
              <Link to="departments"
                className="text-sm font-semibold text-[#06B6D4] hover:text-[#0891B2] inline-flex items-center gap-1.5">
                View all <ArrowRight size={14} strokeWidth={2} />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {departments.slice(0, 6).map((d, i) => (
                <Link key={d.id} to={`departments/${slugify(d.name)}`} data-testid={`cid-home-dept-${d.id}`}
                  className="group relative bg-white rounded-2xl overflow-hidden ring-1 ring-[#E2E8F0] hover:ring-[#FF6B1A] hover:shadow-2xl transition-all">
                  <div className={`h-1.5 w-full ${i % 2 === 0 ? "bg-gradient-to-r from-[#06B6D4] to-[#0891B2]" : "bg-gradient-to-r from-[#FF6B1A] to-[#E55400]"}`} />
                  <div className="p-6">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-md ${i % 2 === 0 ? "bg-gradient-to-br from-[#06B6D4] to-[#0891B2]" : "bg-gradient-to-br from-[#FF6B1A] to-[#E55400]"}`}>
                      <Zap size={18} strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-4 font-['Poppins',sans-serif] font-bold text-[#0B2447] leading-tight">{d.name}</h3>
                    {d.code && (
                      <div className="mt-1 text-[11px] uppercase tracking-wider text-[#475569] font-mono">{d.code}</div>
                    )}
                    {d.course_type && (
                      <span className="mt-3 inline-block text-[10px] tracking-[0.15em] uppercase font-semibold text-[#06B6D4] bg-[#CFFAFE] px-2 py-0.5 rounded">
                        {d.course_type}
                      </span>
                    )}
                    <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#0B2447] group-hover:text-[#FF6B1A] transition-colors">
                      Explore <ArrowRight size={12} strokeWidth={2} className="group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Latest updates */}
      <section className="py-16 md:py-24 bg-white" data-testid="cid-home-info-strip">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#06B6D4] font-semibold">Stay Informed</div>
            <h2 className="mt-2 font-['Poppins',sans-serif] font-bold text-3xl md:text-5xl text-[#0B2447] leading-tight">Latest Updates</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <InfoColumn title="Admissions" eyebrow="Join Us" icon={Megaphone}
              gradient="from-[#FF6B1A] to-[#E55400]" testid="cid-home-admissions"
              items={admissions} empty="Admission notices will appear here once published." />
            <InfoColumn title="Important Links" eyebrow="Resources" icon={Link2}
              gradient="from-[#06B6D4] to-[#0891B2]" testid="cid-home-links"
              items={links} empty="No important links have been published yet." />
            <InfoColumn title="Notices" eyebrow="Communications" icon={Bell}
              gradient="from-[#0B2447] to-[#1B3567]" testid="cid-home-notices"
              items={notices} empty="No notices have been published yet." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 bg-gradient-to-r from-[#0B2447] via-[#1B3567] to-[#06183A] text-white overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#06B6D4]/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[#FF6B1A]/30 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#67E8F9] font-semibold">Take the leap</div>
            <h2 className="mt-2 font-['Poppins',sans-serif] font-bold text-3xl md:text-5xl">
              Your engineering journey starts here.
            </h2>
            <p className="mt-2 text-white/85">Admissions for 2025–26 are open. Speak to our team today.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="admissions"
              className="bg-gradient-to-r from-[#FF6B1A] to-[#E55400] hover:from-[#E55400] hover:to-[#C24A00] text-white font-semibold px-6 py-3 rounded-lg shadow-lg inline-flex items-center gap-2 transition-all">
              Apply Now <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <Link to="contact"
              className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/30 text-white px-6 py-3 rounded-lg inline-flex items-center gap-2">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    <RecentAcademicsWidget docs={academics} theme="cyan" testidPrefix="cid-ac" linkTo="academics" />
    <AlumniStrip items={(data.alumni || []).filter((a) => a.featured)} theme="navy" testid="cid-alumni" registerHref="/cidco/alumni/register" />
    </div>
  );
}

function InfoColumn({ title, eyebrow, icon: Icon, gradient, items, empty, testid }) {
  const list = (items || []).slice(0, 8);
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const isNew = (it) => {
    const ts = it.created_at ? new Date(it.created_at).getTime() : NaN;
    return Number.isFinite(ts) && ts >= sevenDaysAgo;
  };
  return (
    <div data-testid={testid}
      className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm hover:shadow-xl transition-shadow flex flex-col overflow-hidden">
      <div className={`bg-gradient-to-r ${gradient} text-white px-5 py-4 flex items-center gap-3`}>
        <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
          <Icon size={20} strokeWidth={1.5} />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/80 font-semibold">{eyebrow}</div>
          <div className="font-['Poppins',sans-serif] font-bold text-lg leading-tight">{title}</div>
        </div>
      </div>
      <div className="p-4 flex-1">
        {list.length === 0 ? (
          <div className="text-sm text-[#475569] text-center py-10">{empty}</div>
        ) : (
          <ul className="divide-y divide-[#E2E8F0]">
            {list.map((it) => (
              <li key={it.id} className="py-3 flex items-start gap-3" data-testid={`${testid}-item-${it.id}`}>
                <FileText size={14} strokeWidth={1.5} className="text-[#FF6B1A] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0B2447] line-clamp-2 flex items-start gap-1.5 font-medium">
                    <span className="flex-1">{it.name}</span>
                    {isNew(it) && (
                      <span data-testid={`${testid}-new-badge-${it.id}`}
                        className="shrink-0 inline-flex items-center text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-[#FF6B1A] text-white animate-pulse">
                        New
                      </span>
                    )}
                  </div>
                  {(it.pdf_url || it.external_url) && (
                    <a href={it.pdf_url || it.external_url} target="_blank" rel="noopener noreferrer"
                       className="mt-0.5 inline-flex items-center gap-1 text-xs text-[#06B6D4] hover:text-[#0891B2] font-semibold">
                      Open <ExternalLink size={10} strokeWidth={1.5} />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
